// React Hooks:
import React, { useEffect, useState } from "react";
// Redux Toolkit Hooks:
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../../../../app/hooks";
// Redux Toolkit Actions & State:
import {
  getAlternativeFileAction,
  searchAlternativeFileAction,
  resetSearchAlternativeFileAction,
  alternativeFileModalAction,
  setSelectedAlternativeFileAction,
  alternativeFileModal,
} from "../alternativeFileModalSlice";
// MUI Components:
import {
  Modal,
  Box,
  TextField,
  Divider,
  SvgIcon,
  Typography,
  useTheme,
  Skeleton,
} from "@mui/material";
// MUI Styles:,
import { makeStyles } from "@mui/styles";
// SVG Icones:
import { ReactComponent as ArrowIcon } from "../../../../../../../../assets/icons/arrow-down2-icon.svg";

// Type Controller
interface Items {
  id: string;
  name: string;
  type: string;
  size: string;
}

const useStyles = makeStyles((theme) => ({
  backdropStyle: {
    "& .css-i9fmh8-MuiBackdrop-root-MuiModal-backdrop": {
      backdropFilter: "blur(2px)",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  },
  modalContainer: {
    backgroundColor: "#FFFFFF",
    color: theme.palette.text.secondary,
    borderRadius: "8px",
    "&:focus": {
      outline: "none",
    },
  },
  field: {
    borderRadius: "5px",
    backgroundColor: "#F2F2F2",
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#F2F2F2",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: theme.spacing(0),
      "& fieldset": {
        borderColor: "#F2F2F2",
      },
      "&:hover fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
    },
  },
  alternativeItemBox: {
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
  itemsBox: {
    borderRadius: "5px",
    backgroundColor: "transparent",
    color: "transparent",
    "&:hover": {
      transition: "all 0.4s ease",
      cursor: "pointer",
      backgroundColor: "#e1e1e1 !important",
    },
  },
  images: {
    color: theme.palette.secondary.light,
    objectFit: "cover",
  },
}));

const skeletonItems = [1, 2, 3, 4, 5, 6];

const setImageSrc = (item: { name: string; type: string }) => {
  const checkImageExists = (name: string) => {
    try {
      require(`../../../../../../../../assets/megaDL-icones/${name}.png`);
      return true;
    } catch (error) {
      return false;
    }
  };

  if (!!item.type && checkImageExists(item.type)) {
    return require(`../../../../../../../../assets/megaDL-icones/${item.type}.png`);
  } else {
    return require(`../../../../../../../../assets/megaDL-icones/none.png`);
  }
};

const byteToMegabyte = (e: number) => {
  const result = e / (1024 * 1024);
  return result.toFixed(2);
};

const ResponsiveAlternativeFileModal = () => {
  const classes = useStyles();
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const [searchInput, setSearchInput] = useState("");
  const [sortSize, setSortSize] = useState("right");

  const {
    alternativeFile: { actionStatus, items },
    openModal,
  } = useAppSelector(alternativeFileModal);

  useEffect(() => {
    if (openModal) {
      if (searchInput.length <= 2) {
        dispatch(getAlternativeFileAction());
      } else {
        dispatch(
          searchAlternativeFileAction({
            path: "",
            name: searchInput,
            dir: "false",
          })
        );
      }
    }
  }, [dispatch, openModal, searchInput]);

  const sortHandler = () => {
    setSortSize((last) =>
      last === "right"
        ? "down"
        : last === "down"
        ? "up"
        : last === "up"
        ? "right"
        : ""
    );
  };

  const sortSvgIcon = (sort: string) => {
    switch (sort) {
      case "down":
        return "rotate(0deg)";
      case "up":
        return "rotate(180deg)";
      case "right":
        return "rotate(90deg)";
      default:
        return;
    }
  };

  const sortItems = (items: any) => {
    let sortedItems;

    switch (sortSize) {
      case "down":
        sortedItems = [...items].sort((a: any, b: any) => {
          const x = +a.size;
          const y = +b.size;

          return x < y ? -1 : x > y ? 1 : 0;
        });

        return sortedItems;

      case "up":
        sortedItems = [...items].sort((a: any, b: any) => {
          const x = +a.size;
          const y = +b.size;

          return x > y ? -1 : x < y ? 1 : 0;
        });

        return sortedItems;

      default:
        return items;
    }
  };

  return (
    <Modal
      open={openModal}
      onClose={() => dispatch(alternativeFileModalAction(false))}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        zIndex: 1300,
        "&:focus": {
          outline: "none",
        },
        [theme.breakpoints.up("sm")]: {
          display: "none",
        },
      }}
      className={classes.backdropStyle}
    >
      <Box
        sx={{
          width: "80%",
          minHeight: "0px",
          height: "60%",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: 12,
          p: "24px",
          "&:focus-visible": {
            outline: "none",
          },
        }}
        className={classes.modalContainer}
      >
        <TextField
          name="search"
          placeholder="Search File"
          type="text"
          value={searchInput}
          onChange={(e) => {
            dispatch(resetSearchAlternativeFileAction());
            setSearchInput(e.target.value);
          }}
          size={"small"}
          sx={{
            label: { color: (theme) => theme.palette.text.secondary },
            input: {
              padding: "6px",
              fontSize: "14px",
              color: (theme) => theme.palette.text.secondary,
              "&::placeholder": {
                color: (theme) => theme.palette.text.secondary,
              },
              "&:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                transition: " background-color 600000s 0s, color 600000s 0s",
              },
            },
            width: "100%",
          }}
          className={classes.field}
        />

        <Divider
          sx={{
            borderColor: "#dfdfdf",
            margin: "8px 0px 0px 0px",
          }}
        />

        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            m: "6px 0px 6px 0px",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              color: theme.palette.text.secondary,
              mr: "8px",
            }}
          >
            Sort Size
          </Typography>

          <SvgIcon
            component={ArrowIcon}
            inheritViewBox
            sx={{
              width: "12px",
              height: "12px",
              cursor: "pointer",
              color: theme.palette.text.secondary,
              transform: sortSvgIcon(sortSize),
              transition: "all 0.4s",
            }}
            onClick={sortHandler}
          />
        </Box>

        <Box
          sx={{
            width: "100%",
            height: "calc(100% - 82px)",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            [theme.breakpoints.down("md")]: {
              height: "calc(100% - 71.13px)",
            },
          }}
          className={classes.alternativeItemBox}
        >
          {actionStatus === "loading"
            ? skeletonItems.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    width: "calc(100% - 2px)",
                    height: "60px",
                    minHeight: "60px",
                    padding: "16px",
                    margin: "4px 0px 4px 0px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    "&:first-of-type": {
                      marginTop: "0px",
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: theme.palette.background.paper,
                    },
                    "&:nth-of-type(odd)": {
                      backgroundColor: theme.palette.background.paper,
                      filter: "brightness(95%)",
                    },
                    [theme.breakpoints.down("md")]: {
                      width: "100%",
                      height: "55px",
                      minHeight: "55px",
                    },
                  }}
                  className={classes.itemsBox}
                >
                  <Skeleton
                    variant="rounded"
                    width={"35px"}
                    height={"40px"}
                    animation="wave"
                    sx={{
                      bgcolor: "#e8e8e8",
                    }}
                  />

                  <Skeleton
                    variant="rounded"
                    width={"55%"}
                    height={"100%"}
                    animation="wave"
                    sx={{ bgcolor: "#e8e8e8" }}
                  />

                  <Skeleton
                    variant="rounded"
                    width={"15%"}
                    height={"100%"}
                    animation="wave"
                    sx={{ bgcolor: "#e8e8e8" }}
                  />
                </Box>
              ))
            : sortItems(items)?.map((item: Items, index: number) => (
                <Box
                  key={index}
                  sx={{
                    width: "calc(100% - 2px)",
                    height: "60px",
                    minHeight: "60px",
                    padding: "16px",
                    margin: "4px 0px 4px 0px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    "&:first-of-type": {
                      marginTop: "0px",
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: theme.palette.background.paper,
                    },
                    "&:nth-of-type(odd)": {
                      backgroundColor: theme.palette.background.paper,
                      filter: "brightness(95%)",
                    },
                    [theme.breakpoints.down("md")]: {
                      width: "100%",
                      height: "55px",
                      minHeight: "55px",
                    },
                  }}
                  className={classes.itemsBox}
                  onClick={() => {
                    dispatch(setSelectedAlternativeFileAction(item));
                    dispatch(alternativeFileModalAction(false));
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "80%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "40px",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.4s",
                      }}
                      className={classes.images}
                    >
                      <Box
                        component="img"
                        src={setImageSrc(item)}
                        alt={item.name}
                        sx={{
                          width: "100",
                          height: "40px",
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontSize: "12px",
                        p: "0px 0px 0px 8px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: (theme) => theme.palette.text.secondary,
                    }}
                  >
                    {+item.size && `${byteToMegabyte(+item.size)} mb`}
                  </Typography>
                </Box>
              ))}
        </Box>
      </Box>
    </Modal>
  );
};

export default ResponsiveAlternativeFileModal;
